import { IndexDB } from './indexdb';
import { SessionStorage } from './sessionstorage';
import { CacheProps, CacheType, ICacheResult } from './types';

export * from './indexdb';
export * from './sessionstorage';
export * from './types';

export const CACHE_TYPE = {
  sessionStorage: 'session-storage',
  indexdb: 'indexdb',
};

export const incomingCache = <T>(itemOut: T, staleTimeInMillis: number): CacheProps<T> => {
  return {
    data: itemOut,
    staledAt: Date.now() + staleTimeInMillis,
  } as CacheProps<T>;
};

export const Cache = (() => {
  const databaseName = '____@@@gloal_app-database@@@____';
  const tableName = '____@@@global_app-common-table-of-store@@@____';
  const session = new SessionStorage();
  let callbacks: ((data: string) => void)[] = [];

  let isInitDone = false;
  let initDoneData: string;

  // default state time in cache is 10 hours
  const hours = 16;
  const minutes = 60;
  const seconds = 60;
  const milliseconds = 1000;
  let DEFAULT_STALE_TIME = hours * minutes * seconds * milliseconds;

  const NERVER_EXPIRED_KEYS: string[] = [];

  /**
   * clean up the staled entries
   */
  const cleanStaledEntries = () => {
    (async () => {
      const entries = await getAllIndexDb();
      const keys = Object.keys(entries);
      const currentTime = Date.now();
      for await (const key of keys) {
        if (entries[key].staledAt < currentTime && !NERVER_EXPIRED_KEYS.includes(key)) {
          await indexdb.delete(key);
        }
      }
      // scheduler runs every 1 hour
      setTimeout(cleanStaledEntries, minutes * seconds * milliseconds);
    })();
  };

  const onDone = (edata: string) => {
    isInitDone = true;
    initDoneData = edata;
    callbacks.forEach((cb) => cb(edata));

    // start cleaning scheduler
    cleanStaledEntries();
  };
  const indexdb = new IndexDB(databaseName, tableName, onDone);

  const removeEmptyKeys = (o: any) => {
    const data = o || {};
    const keys = Object.keys(data);
    keys.forEach((key: string) => {
      if (Object.keys(data[key]).length === 0) {
        delete data[key];
      }
    });
    return data;
  };

  return {
    /**
     * Return ICacheResult<T> to indicate the upsert operation success or not
     * @param key the key of the cache
     * @param value value we want to cache
     * @param cacheType (one of `session-storage` | `indexdb`)
     * @param staledInMillis the expired time of the item we cache
     * @returns Promise<ICacheResult<T>>
     */
    async upsert<T>(
      key: string,
      value: T,
      cacheType: CacheType,
      staledInMillis: number | null
    ): Promise<ICacheResult<T>> {
      let result: ICacheResult<T> = { success: true };
      // session
      switch (cacheType) {
        case CACHE_TYPE.sessionStorage: {
          const { data = {} } = await session.get<{
            [key: string]: CacheProps<T>;
          }>(tableName);

          const item = incomingCache<T>(value, staledInMillis || DEFAULT_STALE_TIME);
          const nextValue: { [key: string]: CacheProps<T> } = {
            ...data,
            [key]: item,
          };
          result = await session.upsert(tableName, nextValue);
          break;
        }
        case CACHE_TYPE.indexdb: {
          const item = incomingCache<T>(value, staledInMillis || DEFAULT_STALE_TIME);
          result = (await indexdb.upsert<CacheProps<T>>(key, item)) as any;
          break;
        }
        default:
          break;
      }
      return result;
    },

    async getAll<T>(
      cacheType?: CacheType
    ): Promise<ICacheResult<{ [key: string]: CacheProps<T> }>> {
      const result: ICacheResult<{ [key: string]: CacheProps<T> }> = {
        success: true,
      };
      let indexDt: { [key: string]: CacheProps<any> } = {};
      let sessionDt: { [key: string]: CacheProps<any> } = {};

      switch (cacheType) {
        case CACHE_TYPE.indexdb: {
          indexDt = await getAllIndexDb<{ [key: string]: string }>();
          break;
        }
        case CACHE_TYPE.sessionStorage: {
          sessionDt = await getAllSession<{ [key: string]: string }>();
          break;
        }
        default: {
          // session
          sessionDt = await getAllSession<{ [key: string]: string }>();

          // indexdb
          indexDt = await getAllIndexDb<{ [key: string]: string }>();
        }
      }
      result.data = removeEmptyKeys({ ...sessionDt, ...indexDt });

      return result;
    },

    /**
     * Return ICacheResult<any to indicate the upsert operation success or not
     * @param key the key we want to delete from cache
     * @param cacheType the type of cache (`session-storage` | `indexdb`)
     * @returns Promise<ICacheResult<any>>
     */
    async delete(key: string, cacheType: CacheType): Promise<ICacheResult<any>> {
      let result: ICacheResult<any> = { success: true };
      switch (cacheType) {
        case CACHE_TYPE.indexdb: {
          result = await indexdb.delete(key);
          break;
        }
        case CACHE_TYPE.sessionStorage: {
          result = await session.delete(key);
          break;
        }
        default: {
          await indexdb.delete(key);
          await session.delete(key);
        }
      }
      return result;
    },

    /**
     * Return a value associated with the given key in cache
     * @param key the key we want to get its data
     * @param cacheType type of cache: `session-storage` | `indexdb`
     * @returns ICacheResult<CacheProps<T>>
     */
    async get<T>(key: string, cacheType: CacheType): Promise<ICacheResult<CacheProps<T>>> {
      const result: ICacheResult<CacheProps<T>> = { success: true };
      switch (cacheType) {
        case CACHE_TYPE.indexdb: {
          const { data: entry } = await indexdb.get<CacheProps<T>>(key);
          result.data = entry;
          break;
        }
        case CACHE_TYPE.sessionStorage: {
          const { data: entry } = await session.get<CacheProps<T>>(key);
          result.data = entry;
          break;
        }
        default:
          break;
      }
      return result;
    },

    /**
     * Notify the indexdb has been initilized successfully
     * @param cb the callback function to be triggered once indexDb initialized successfully
     * @returns
     */
    addInitDoneListeners(cb: (edata: string) => void) {
      if (isInitDone) {
        cb(initDoneData);
        return;
      }
      callbacks = [...callbacks, cb];
    },

    /**
     * Set default staled time for every entry in indexdb
     * @param staleTimeInMillis milliseconds by which a record in indexdb is considered staled.
     */
    setStaleTime(staleTimeInMillis: number) {
      DEFAULT_STALE_TIME = staleTimeInMillis;
    },

    /**
     * Add keys will be removed once their data are staled
     * @param keys a list of keys we don't want their entries will be staled
     */
    addNeverExpiredKeys(keys: string[]): void {
      (keys || []).forEach((key: string) => {
        if (!NERVER_EXPIRED_KEYS.includes(key)) {
          NERVER_EXPIRED_KEYS.push(key);
        }
      });
    },
  };

  async function getAllIndexDb<T>() {
    const { data: entries = {} } = await indexdb.getAllRows<{
      [key: string]: CacheProps<T>;
    }>();
    return entries;
  }

  async function getAllSession<T>() {
    const { data: entries = {} } = await session.get<{
      [key: string]: CacheProps<T>;
    }>(tableName);
    return entries;
  }
})();
